import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import {
  ChangeDetectorRef,
  DestroyRef,
  Component,
  inject,
  Injector,
  NgZone,
  OnInit,
  type QueryList,
  signal,
  type WritableSignal,
  ViewChildren,
} from '@angular/core';
import { Application } from '@modules/common/types/application';
import type { Account } from '@frontendlib/common/types/account';
import { AccountService } from '@frontendlib/common/services/account.service';
import { Constants } from '@frontendlib/common/types/Constants';
import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { DragNDropService } from '@modules/drag-n-drop/services/drag-n-drop.service';
import { DragData, DragDataTypes, dndStitchPredicate } from '@modules/drag-n-drop/types/drag-data';
import {
  type TransitionCheckableMessage,
  type TransitionResult,
  evaluateTransitionBatch,
} from '@modules/messages/dnd/mail-transitions';
import { Message } from '@modules/messages/types/message';
import { MessageFolder } from '@modules/messages/types/message-folder';
import { MessagesCounts } from '@modules/messages/types/messages-counts';
import { VirtualFolder } from '@modules/messages/types/virtual-folder';
import { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { FoldersService } from '@modules/files/services/folders.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { asyncScheduler, catchError, combineLatest, EMPTY, forkJoin, from, merge, of, type Observable } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  last,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { type MailRailSections, MailRailFolderTrees } from '@modules/messages/services/mail-rail-folder-trees.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { KnowledgeItemsRailComponent } from '@modules/common/components/knowledge-items-rail/knowledge-items-rail.component';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { Space } from '@modules/settings/types/space';
import { Team } from '@modules/settings/types/team';
import { workspaceColor, workspacePalette } from '@modules/common/utils/workspace-color';

/**
 * One emission of the rail's identity source.
 *
 * `identityFailed` marks the ONE arm that has no identity answer at all — the first `with-invites`
 * of a session failing with no last known list. It exists so that arm can keep the counts stream
 * alive without its `items: []` reaching `resolveMemberIdentity` (#1151 review F1); every other
 * emission carries a real list and settles the identity normally.
 */
type IdentityEmission = { account: Account; items: Space[]; identityFailed?: boolean };

/** One refresh of the rail's numbers: the unread counts, or the Drafts totals, per visible mailbox. */
type RailCountsUpdate =
  | { kind: 'unread'; myWorkspace: MessagesCounts | undefined; team: MessagesCounts | undefined }
  | { kind: 'drafts'; myWorkspace: number | undefined; team: number | undefined };

@Component({
  selector: 'app-messages-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class MessagesSideBarComponent
  extends BaseSidebarComponent<MessageFolder, Message, VirtualFolder>
  implements OnInit
{
  protected readonly workspaceColor = workspaceColor;
  protected readonly workspacePalette = workspacePalette;
  public readonly SidebarSplitViewKey = SidebarSplitViewKey;
  public myWorkspaceCounts: MessagesCounts | undefined;
  public teamCounts: MessagesCounts | undefined;
  private myWorkspaceUnread: MessagesCounts | undefined;
  private teamUnread: MessagesCounts | undefined;
  private myWorkspaceDrafts: number | undefined;
  private teamDrafts: number | undefined;
  application = Application.mail;
  sidebarSplitViewKey = SidebarSplitViewKey.mail;
  selfDragDataTypes = [DragDataTypes.message, DragDataTypes.messageFolder];

  // Order per SBUX-943, and pinned by `mail-folder-order.spec.ts` — the panel's folder menu and the
  // MoveTo/CopyTo targets follow this list, and `privateItems` repeats its order.
  public baseItems: SidebarItem[] = [
    { title: 'My Inbox', id: 'inbox', icon: 'sapphire-inbox' },
    { title: 'VIP', id: 'vip', icon: 'sapphire-vip' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Drafts', id: 'draft', icon: 'sapphire-draft' },
    { title: 'Scheduled', id: 'scheduled', icon: 'sapphire-scheduled-send' },
    { title: 'Sent', id: 'sent', icon: 'sapphire-sent' },
    // Deliberately not sapphire-bulk — Other Mail has to read as distinct from Bulk in the rail.
    { title: 'Other Mail', id: 'other_mail', icon: 'sapphire-email' },
    { title: 'Bulk', id: 'bulk', icon: 'sapphire-bulk' },
    { title: 'Spam', id: 'spam', icon: 'sapphire-spam' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];

  public readonly primaryItems: SidebarItem[] = ['inbox', 'sent', 'draft', 'scheduled', 'flagged', 'vip'].flatMap(
    (id) => this.baseItems.filter((item) => item.id === id),
  );
  public readonly moreItems: SidebarItem[] = this.baseItems.filter(
    ({ id }) => !['inbox', 'sent', 'draft', 'scheduled', 'flagged', 'vip'].includes(id),
  );
  // Draft is a message state, not a folder, so the team mailbox can show it too — Figma 102-82818 draws
  // it first under the team's View more. Personal keeps Draft among its primary rows, so the two More
  // lists differ by exactly that row and `moreItems` must stay untouched.
  public readonly teamMoreItems: SidebarItem[] = [
    ...this.baseItems.filter(({ id }) => id === 'draft'),
    ...this.moreItems,
  ];
  public readonly privateItems: SidebarItem[] = [
    { title: 'Inbox', id: 'inbox', icon: 'sapphire-inbox' },
    { title: 'VIP', id: 'vip', icon: 'sapphire-vip' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Drafts', id: 'draft', icon: 'sapphire-draft' },
    { title: 'Scheduled', id: 'scheduled', icon: 'sapphire-scheduled-send' },
    { title: 'Sent', id: 'sent', icon: 'sapphire-sent' },
    { title: 'Other Mail', id: 'other_mail', icon: 'sapphire-email' },
    { title: 'Bulk', id: 'bulk', icon: 'sapphire-bulk' },
    { title: 'Spam', id: 'spam', icon: 'sapphire-spam' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];
  public readonly teamPrimaryItems: SidebarItem[] = ['inbox', 'sent', 'scheduled', 'flagged', 'vip'].flatMap((id) =>
    this.baseItems
      .filter((item) => item.id === id)
      .map((item) => (item.id === 'inbox' ? { ...item, title: 'Inbox' } : item)),
  );

  /** "Open Other Mail" from a My Workspace row's menu: the member mailbox's view, as the row click does. */
  protected readonly openMemberView = (filter: VirtualFolder): void => {
    if (this.moreItems.some(({ id }) => id === filter)) this.moreExpanded.set(true);
    this.selectFilter(filter, this.currentMemberMailbox);
  };

  /** "Open Other Mail" from a Team Workspace row's menu: the team mailbox's view, as the row click does. */
  protected readonly openTeamView = (filter: VirtualFolder): void => {
    if (this.teamMoreItems.some(({ id }) => id === filter)) this.teamMoreExpanded.set(true);
    this.selectFilter(filter, this.currentTeamMailbox);
  };

  /** "Open Other Mail" from the personal rail's Inbox menu: the personal view, as the row click does. */
  protected readonly openPersonalView = (filter: VirtualFolder): void => this.selectFilter(filter);

  protected readonly myWorkspaceExpanded = signal(true);
  protected readonly moreExpanded = signal(false);
  protected readonly teamWorkspaceExpanded = signal(true);
  protected readonly currentTeamWorkspaceExpanded = signal(true);
  protected readonly teamMoreExpanded = signal(false);

  /**
   * "Open folder" from the search popup, for a folder whose section is collapsed — and so has no folder tree mounted:
   * the section opens, as a person opens it before clicking the folder, and the tree then runs the open itself
   * (searchopen r4, Codex on #2758, reproduced live).
   */
  private readonly railFolderTrees = inject(MailRailFolderTrees, { optional: true });
  private readonly railSections: MailRailSections = { expandFor: (tenantId) => this.expandSectionFor(tenantId) };
  private readonly railSectionsRegistration = inject(DestroyRef).onDestroy(() =>
    this.railFolderTrees?.unregisterSections(this.railSections),
  );
  protected currentTeamWorkspace: Space | undefined;
  protected currentMemberMailbox: Team | undefined;
  protected currentTeamMailbox: Team | undefined;
  protected currentTeamWorkspaceInitials = '';
  /**
   * The last spaces list this rail actually received, so a LATE `with-invites` failure can be
   * served from it rather than ending the stream that feeds the rail counts (#1065 ROW 2).
   */
  private lastKnownSpaces: Space[] | undefined;

  public isDragging = false;
  private currentDragData: DragData | null = null;
  /** Tracks which sidebar item currently owns the drop hint, so leave events from stale items don't clear it. */
  private hintedItemId: string | null = null;

  protected readonly spaceContextStore = inject(SpaceContextStore);
  /** Team mail volume and Inbox options are team features: a personal login never sees them. */
  protected readonly accountKind = inject(AccountKindService);
  private readonly messagesService = inject(MessagesService);
  // The base class types `stateService` as `BaseAppStateService`; the mailbox gate lives on mail's
  // own state service, and `MailAppContextDirective` maps both tokens to the same instance.
  private readonly mailState = inject(MailAppStateService);
  private readonly accountService = inject(AccountService);
  private readonly messageFoldersService = inject(FoldersService);
  private readonly spacesService = inject(SpacesService);
  private readonly stitchEventService = inject(StitchEventsService);
  private readonly dragNDropService = inject(DragNDropService);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChildren(KnowledgeItemsRailComponent) private knowledgeRails?: QueryList<KnowledgeItemsRailComponent>;

  /** Narrows DragData items to TransitionCheckableMessage[] using the runtime type guard. */
  private get draggedMessages(): readonly TransitionCheckableMessage[] {
    return this.currentDragData?.data?.filter(MessageSummaryDto.is) ?? [];
  }

  /** Evaluates a DnD operation for the given target using transition rules. */
  private evaluate(targetId: string): TransitionResult | null {
    if (this.draggedMessages.length === 0) return null;
    return evaluateTransitionBatch(this.draggedMessages, targetId);
  }

  /** Returns true if the spec disallows this drop. */
  public override isDropDisabled = (itemId: string): boolean => {
    if (this.currentDragData?.type !== DragDataTypes.message) return false;
    return !(this.evaluate(itemId)?.allowed ?? false);
  };

  /** Checks whether a drop is blocked by transition rules. */
  public isDropBlocked = (itemId: string): boolean => {
    if (this.currentDragData?.type !== DragDataTypes.message) return false;
    return !(this.evaluate(itemId)?.allowed ?? true);
  };

  public override dndPredicate = (dragData: DragData): boolean => dndStitchPredicate(dragData.type);

  private expandSectionFor(tenantId: string): boolean {
    if (!this.currentTeamWorkspace) {
      // The personal rail has no sections: its one tree is always mounted, and an open made before it has its folders
      // waits for them. Inside a team before the identity answers, the team's sections are not drawn yet: the open
      // waits for their trees (Codex on #2758, both reproduced live with the read held).
      return (
        tenantId === this.spaceContextStore.contextId() || this.spaceContextStore.type() === SpaceType.SHARED_SPACE
      );
    }
    if (this.currentMemberMailbox?.id === tenantId) {
      this.myWorkspaceExpanded.set(true);
      return true;
    }
    if (this.currentTeamWorkspace?.id === tenantId) {
      this.teamWorkspaceExpanded.set(true);
      this.currentTeamWorkspaceExpanded.set(true);
      return true;
    }
    return false;
  }

  constructor() {
    super(inject(Injector));
  }

  ngOnInit() {
    super.ngOnInit();
    this.railFolderTrees?.registerSections(this.railSections);

    const accountAndSpaces$ = this.accountService.getAccount(true).pipe(
      switchMap((account) =>
        this.spacesService.listWithInvites({ archived: false, limit: 100 }, { account }).pipe(
          // Annotated, not inferred: without it the healthy arm's type and the catchError's below
          // form a union, and `identityFailed` is readable on neither half of it.
          map(({ items }): IdentityEmission => {
            this.lastKnownSpaces = items;
            return { account, items };
          }),
          // Invariant 3 of `devdoc/specs/workspace-tenant-scoping.md`: a failed identity lookup
          // degrades the surface to its personal arm rather than gating it forever. It belongs on
          // the identity source, and NOT on the `subscribe` below — that subscription's `switchMap`
          // also carries `getMessagesCount`, so an error arm there would let a failing counts
          // request move the user's mailbox to personal.
          //
          // On the INNER lookup, not the outer stream (#1065 ROW 2). `getAccount(true)` re-emits on
          // every `AccountService.update()`, so a LATE failure of the same GET used to reach a
          // catchError that returned EMPTY: the mailbox flipped to personal with the member
          // selection still stored, `accountAndSpaces$` COMPLETED, and the rail counts stopped for
          // the rest of the session. Catching here leaves the outer stream alive to try again, and
          // `degradeToPersonalMailbox()` is refused once the identity has resolved — so a late
          // failure re-serves the last known spaces instead of undoing a resolved mailbox.
          catchError((): Observable<IdentityEmission> => {
            // SERVE FIRST, degrade only when there is nothing to serve (#1065 r2 ROW 5). Degrading
            // unconditionally ticked `degradeChanged`, `selectedTeamId()` emitted null and ONE
            // unstamped read went out before the served list corrected it — a one-frame flash of the
            // #1049 shape, reachable in a context that has not resolved yet. The list is every space,
            // not this one, so it settles a context the failure interrupted as well as the last.
            //
            if (this.lastKnownSpaces) {
              return of({ account, items: this.lastKnownSpaces });
            }
            // With nothing known, invariant 3 degrades to the personal arm and settles NOTHING. The
            // emission keeps the count stream alive — that is the whole of this round's requirement —
            // and `identityFailed` is what stops it reaching `resolveMemberIdentity`.
            //
            // Never a plain `items: []`: that clears `currentTeamWorkspace`, releases the gate
            // through `resolveMemberIdentity(null)` with no migration, and renders the flat arm —
            // the very producer of the legacy `null` the previous round closed. #1151 review F1
            // measured what it costs here: `releaseTenantIdentity()` undoes the degrade one
            // statement later, in this same emission, so `effectiveTenantId()` answers the SPACE
            // while the rail draws the personal arm.
            this.mailState.degradeToPersonalMailbox();
            return of({ account, items: [], identityFailed: true });
          }),
        ),
      ),
    );

    this.dragNDropService
      .getDraggingDataChanges()
      .pipe(takeUntil(this.alive))
      .subscribe((data) => {
        this.isDragging = !!data;
        this.currentDragData = data;
        if (!data) {
          this.hintedItemId = null;
        }
        if (!NgZone.isInAngularZone()) {
          this.cdr.detectChanges();
        }
      });

    // Both mailbox summaries stay independently scoped even though both sections are visible at once.
    const contextId$ = this.spaceContextStore.contextId$.pipe(
      startWith(this.spaceContextStore.contextId()),
      distinctUntilChanged(),
    );

    combineLatest([accountAndSpaces$, contextId$])
      .pipe(
        switchMap(([{ account, items, identityFailed }, contextId]) => {
          if (identityFailed) {
            // Draw the personal arm, settle nothing: the degrade the catchError just made has to
            // still be standing when this emission ends. `setCurrentTeamWorkspace` would call
            // `resolveMemberIdentity(null)` → `repairLegacyTenantSelection(null)` (a no-op, the
            // argument is null) → `releaseTenantIdentity()`, which deletes this context from
            // `degradedContexts` and adds it to `resolvedContexts` — "the team on the wire and me
            // on screen", the #961 round-4 shape `app-tenant-scope.service.ts:effectiveTenantId`
            // names, plus a context that refuses every later degrade for its lifetime.
            //
            // `combineLatest` replays this tuple, so a context switch after a failed first lookup
            // also draws the personal arm and resolves nothing — the base returned EMPTY and drew
            // no arm at all, so nothing that used to resolve stops resolving.
            this.applyTeamWorkspace([], contextId);
          } else {
            this.setCurrentTeamWorkspace(items, contextId);
          }
          const teamId = this.currentTeamWorkspace?.id;
          const memberMailboxTenantId = this.currentMemberMailbox?.id;
          const myWorkspaceEventsTenantId = teamId ? memberMailboxTenantId : account.id;

          // `undefined` = no My Workspace mailbox in this team; `null` = the signed-in user's own mailbox.
          const myWorkspaceMailbox = teamId ? memberMailboxTenantId : null;
          const counters$ = merge(
            myWorkspaceEventsTenantId
              ? this.stitchEventService.getEvents(myWorkspaceEventsTenantId, StitchType.MESSAGE, [
                  SignalStitchEventType.COUNTERS,
                ])
              : EMPTY,
            teamId
              ? this.stitchEventService.getEvents(teamId, StitchType.MESSAGE, [SignalStitchEventType.COUNTERS])
              : EMPTY,
          );

          const unreadCounts$ = counters$.pipe(
            startWith(null),
            // trailing: true keeps the settled ES count after a burst, for both visible mailboxes.
            throttleTime(Constants.DEBOUNCE_EVENT_COUNTS_RELOAD, asyncScheduler, { leading: true, trailing: true }),
            tap(() => {
              this.knowledgeRails?.forEach((rail) => rail.reload());
            }),
            switchMap(() =>
              forkJoin({
                myWorkspace:
                  myWorkspaceMailbox === undefined ? of(undefined) : this.getMessagesCountSafely(myWorkspaceMailbox),
                team: teamId ? this.getMessagesCountSafely(teamId) : of(undefined),
              }),
            ),
            map((counts): RailCountsUpdate => ({ kind: 'unread', ...counts })),
          );

          // The Drafts total is read off the list, not ES, so it can follow this app's own writes (a draft
          // saved, sent or deleted) at once instead of waiting for the indexer's COUNTERS signal.
          const draftTotals$ = merge(counters$, this.messagesService.getRefresh()).pipe(
            startWith(null),
            throttleTime(Constants.DEBOUNCE_EVENT_COUNTS_RELOAD, asyncScheduler, { leading: true, trailing: true }),
            switchMap(() =>
              forkJoin({
                myWorkspace:
                  myWorkspaceMailbox === undefined ? of(undefined) : this.getDraftsTotalSafely(myWorkspaceMailbox),
                team: teamId ? this.getDraftsTotalSafely(teamId) : of(undefined),
              }),
            ),
            map((totals): RailCountsUpdate => ({ kind: 'drafts', ...totals })),
          );

          return merge(unreadCounts$, draftTotals$);
        }),
        takeUntil(this.alive),
      )
      .subscribe((update) => {
        if (update.kind === 'unread') {
          this.myWorkspaceUnread = update.myWorkspace;
          this.teamUnread = update.team;
        } else {
          this.myWorkspaceDrafts = update.myWorkspace;
          this.teamDrafts = update.team;
        }
        this.myWorkspaceCounts = MessagesSideBarComponent.withDraftsTotal(this.myWorkspaceUnread, this.myWorkspaceDrafts);
        this.teamCounts = MessagesSideBarComponent.withDraftsTotal(this.teamUnread, this.teamDrafts);
      });
  }

  /**
   * One mailbox's rail counts: the unread counts with `draft` replaced by the mailbox's Drafts total. The
   * endpoint's own `draft` is an unread count; the Drafts row shows how many drafts there are.
   */
  private static withDraftsTotal(
    unread: MessagesCounts | undefined,
    drafts: number | undefined,
  ): MessagesCounts | undefined {
    if (drafts === undefined) {
      return unread;
    }
    return { ...unread, draft: drafts };
  }

  /** Keeps one mailbox count failure from ending or blanking the other mailbox stream. */
  private getMessagesCountSafely(mailboxTenantId: string | null): Observable<MessagesCounts | undefined> {
    return this.messagesService.getMessagesCount(mailboxTenantId).pipe(catchError(() => of(undefined)));
  }

  /** A failed Drafts total leaves the row without a number; it never blanks the unread counts. */
  private getDraftsTotalSafely(mailboxTenantId: string | null): Observable<number | undefined> {
    return this.messagesService.getDraftsTotal(mailboxTenantId).pipe(catchError(() => of(undefined)));
  }

  private setCurrentTeamWorkspace(items: Space[], contextId: string | undefined): void {
    this.applyTeamWorkspace(items, contextId);

    // Round 1 persisted My Workspace as null (personal mail). Null is not a valid mailbox selection
    // inside a team anymore, so the member tenant this DTO supplies both migrates that state and
    // releases the first-read gate — one call, because the rail cannot test the stored value itself:
    // its `currentTeamId` comes from the gated stream (#1049).
    this.mailState.resolveMemberIdentity(this.currentMemberMailbox?.id ?? null);
  }

  /**
   * The rail's arm for `items` in `contextId`, with the mailbox identity left exactly as it is.
   *
   * Split out of {@link setCurrentTeamWorkspace} so the degraded arm has a way to draw the personal
   * rail without settling anything (#1151 review F1). Every caller that HAS an identity answer calls
   * `setCurrentTeamWorkspace`, which is this plus the one resolver call.
   */
  private applyTeamWorkspace(items: Space[], contextId: string | undefined): void {
    this.currentTeamWorkspace = items.find(
      ({ currentUserStatus, id }) => currentUserStatus === 'accepted' && id === contextId,
    );
    this.currentMemberMailbox = this.currentTeamWorkspace?.myMailboxTenantId
      ? new Team({ id: this.currentTeamWorkspace.myMailboxTenantId })
      : undefined;
    this.currentTeamMailbox = this.currentTeamWorkspace ? new Team({ id: this.currentTeamWorkspace.id }) : undefined;

    this.currentTeamWorkspaceInitials =
      this.currentTeamWorkspace?.title
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase() ?? '';
  }

  /**
   * Actions
   */

  // Blocked targets are intentionally kept in the droppable registry (no appDroppableDisabled)
  // so the DnD service detects hover and we can show forbidden hints on the drag ghost.
  // The guard here silently rejects the drop; the drag ghost snaps back via returnBackMotion.
  handleMove(dragData: DragData, sidebarFolder: VirtualFolder) {
    if (this.isDropBlocked(sidebarFolder)) {
      return;
    }

    if (dragData.type === DragDataTypes.message) {
      this.handleMessageMove(dragData, sidebarFolder);
    } else if (dragData.type === DragDataTypes.messageFolder) {
      const result = MessageFolder.getChangesForVirtualFolder(sidebarFolder);
      if (!result) return;
      const { changes, message } = result;
      this.messageFoldersService.bunchUpdate({ ids: this.getIds(dragData.data) }, changes, { message });
    }
  }

  /**
   * Orchestrates moving messages to a target virtual folder.
   * Uses transition rules to determine which states need clearing before applying the target.
   */
  private handleMessageMove(dragData: DragData, sidebarFolder: VirtualFolder): void {
    const ids = this.getIds(dragData.data);
    const result = this.evaluate(sidebarFolder);
    if (!result) return;

    if (result.clears.length > 0) {
      from(result.clears)
        .pipe(
          concatMap((flag) => this.getClearOperation(ids, flag) ?? EMPTY),
          last(),
        )
        .subscribe(() => this.applyTarget(ids, sidebarFolder));
      return;
    }

    this.applyTarget(ids, sidebarFolder);
  }

  /** Fields that are backend-managed and must not be sent via bunchUpdate. */
  private static readonly BACKEND_MANAGED_FIELDS: ReadonlySet<string> = new Set(['state', 'direction']);

  /** Applies the target folder changes. Handles trash/archive via dedicated APIs, everything else via bunchUpdate. */
  private applyTarget(ids: string[], sidebarFolder: VirtualFolder): void {
    if (sidebarFolder === 'deleted') {
      this.messagesService.delete({ ids }, true);
      return;
    }
    if (sidebarFolder === 'archived') {
      this.messagesService.archive({ ids }, true);
      return;
    }

    const result = Message.getChangesForVirtualFolder(sidebarFolder);
    if (!result) return;

    const { changes, message } = result;
    // Filter out backend-managed fields (state, direction) — they can't be set via bunchUpdate
    const safeChanges = Object.fromEntries(
      Object.entries(changes).filter(([key]) => !MessagesSideBarComponent.BACKEND_MANAGED_FIELDS.has(key)),
    );
    if (Object.keys(safeChanges).length === 0) return;

    this.messagesService.bunchUpdate({ ids }, safeChanges, { message });
  }

  /** Returns an Observable that clears the given flag/state, or null if not applicable. */
  private getClearOperation(ids: string[], flag: string): Observable<unknown> | null {
    switch (flag) {
      case 'deleted':
        return this.messagesService.delete({ ids }, false);
      case 'archived':
        return this.messagesService.archive({ ids }, false);
      case 'scheduled':
        return this.messagesService.unscheduleMessages({ ids });
      case 'flagged':
        return this.messagesService.flag({ ids }, false);
      case 'pinned':
        return this.messagesService.pin({ ids }, false);
      case 'followed':
        return this.messagesService.followUp({ ids }, null);
      case 'snoozed':
        return this.messagesService.snooze({ ids }, null);
      case 'user_folder':
        return this.messagesService.bunchUpdate({ ids }, { folderId: null }, { toast: false, emit: false });
      default:
        return null;
    }
  }

  /** Returns a human-readable hint explaining why a drop on the given target is blocked. */
  public getDropDisabledReason(targetId: string): string {
    const result = this.evaluate(targetId);
    if (!result || result.allowed) return '';
    if (this.isDraggingMultiple() && result.hintPlural) return result.hintPlural;
    return result.hint ?? "Can't move here";
  }

  /** Returns the icon name for a sidebar item by its id. */
  private getItemIcon(itemId: string): Icon {
    return this.baseItems.find((item) => item.id === itemId)?.icon ?? 'sapphire-forbidden';
  }

  /** Returns true when multiple messages are being dragged. */
  private isDraggingMultiple(): boolean {
    return (this.currentDragData?.data?.length ?? 0) > 1;
  }

  /**
   * Called by each sidebar item's (appDroppableEnter) when the drag ghost enters it.
   * Evaluates the drop and sets the appropriate hint on the drag ghost.
   */
  protected onItemDragEnter(_dragData: DragData, itemId: string): void {
    this.hintedItemId = itemId;

    if (this.currentDragData?.type !== DragDataTypes.message) {
      this.dragNDropService.setDropHint(null);
      return;
    }

    const result = this.evaluate(itemId);
    if (!result) {
      this.dragNDropService.setDropHint(null);
      return;
    }

    const plural = this.isDraggingMultiple();

    if (!result.allowed) {
      const text = (plural && result.hintPlural) || result.hint || "Can't move here";
      this.dragNDropService.setDropHint({ text, icon: 'sapphire-forbidden', stacked: plural });
      return;
    }

    if (result.hint) {
      const text = (plural && result.hintPlural) || result.hint;
      this.dragNDropService.setDropHint({ text, icon: this.getItemIcon(itemId) });
      return;
    }

    this.dragNDropService.setDropHint(null);
  }

  /**
   * Called by each sidebar item's (appDroppableLeave) when the drag ghost leaves it.
   * Only clears the hint if this item was the one that set it (prevents flicker when moving between items).
   */
  protected onItemDragLeave(itemId: string): void {
    if (this.hintedItemId === itemId) {
      this.hintedItemId = null;
      this.dragNDropService.setDropHint(null);
    }
  }

  handleMore(item: Message) {
    this.stateService.setMainView(item);
  }

  /** Expands or collapses the current workspace's Mail destinations without changing space context. */
  protected toggleCurrentTeamWorkspace(): void {
    this.toggleSection(this.currentTeamWorkspaceExpanded);
  }

  /** A section heading's click. Collapsing a section also cancels an "Open folder" still waiting for it. */
  protected toggleSection(expanded: WritableSignal<boolean>): void {
    if (expanded()) {
      this.railFolderTrees?.cancelPending();
    }
    expanded.set(!expanded());
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder): Message {
    const { changes } = Message.getChangesForVirtualFolder(sidebarFolder);

    return new Message({ ...changes, title: dragData.data[0]['title'] });
  }
}
